import { alpha, Box, BoxProps, Fade, Menu, PaperProps, Popover, styled, TooltipProps } from "@mui/material";
import { ModalProps } from "@mui/material/Modal";
import type { PopoverActions } from "@mui/material/Popover/Popover";
import { useElementResizeAndViewportOverflow } from "@sinch/hooks/utilities/useElementResizeAndViewportOverflow";
import React, {
  ForwardedRef,
  forwardRef,
  KeyboardEventHandler,
  ReactElement,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";

/**
 * Wrapper for edited content
 */
const EditableBox = styled(Box, {
  name: "EditableBox",
  shouldForwardProp: (prop) => prop !== "focused" && prop !== "error",
})<BoxProps & { focused?: boolean; error?: boolean }>(({ theme, focused, error }) => ({
  paddingLeft: theme.spacing(0.5),
  paddingRight: theme.spacing(0.5),
  borderStyle: "solid",
  borderWidth: 1,
  borderRadius: `${theme.shape.borderRadius}px !important`,
  borderColor: "transparent",
  cursor: "pointer",
  marginLeft: theme.spacing(-0.5),

  [theme.breakpoints.down("sm")]: {
    borderColor: theme.palette.grey[400],
    minWidth: "100%",
  },
  "&:hover": {
    backgroundColor: theme.palette.custom.blue[50],
    borderColor: theme.palette.primary.main,
  },
  "&:focus-visible": {
    boxShadow: `0px 0px 0px 4px ${theme.palette.custom.blue[100]}`,
    outline: "none",
  },
  ...(focused
    ? {
        backgroundColor: theme.palette.background.paper,
        borderColor: theme.palette.primary.main,
        boxShadow: `0px 0px 4px ${alpha(theme.palette.primary.main, 0.25)}`,
      }
    : {}),
  ...(error
    ? {
        borderColor: theme.palette.error.main,
      }
    : {}),
}));

export interface HtmlTooltipProps extends Omit<TooltipProps, "onClose"> {
  /**
   * Call when user click on box
   */
  onClick?: () => void;
  /**
   * Call when user click close tooltip or click away
   */
  onClose?: ModalProps["onClose"];
  /**
   * If true, edit button will be full width, default true
   */
  fullWidth?: boolean;
  /**
   * Props applied to EditableBox
   */
  boxProps?: BoxProps;

  /**
   * Controls the overlay presentation style. "popover" anchors a floating panel,
   * "menu" uses MUI Menu semantics. Default: "popover"
   */
  variant?: "popover" | "menu";

  /**
   * When true, renders the EditableBox border in error color
   */
  error?: boolean;

  /**
   * When true, destroys the overlay content when closed instead of keeping it mounted.
   * Useful for resetting internal state on close
   */
  unmountOnClose?: boolean;

  /**
   * Props forwarded to the MUI Paper element inside the overlay
   */
  paperProps?: PaperProps;
}

/**
 * Imperative handle exposed via forwardRef, allowing the parent to trigger
 * animations on the overlay paper without controlling state
 */
export type QuickEditButtonActions = {
  /**
   * Plays a horizontal shake animation on the overlay paper element,
   * useful for drawing attention when a backdrop click is blocked
   */
  shake: () => void;
};

/**
 * Provide wrapper to show rich content tooltip. Show box for open tooltip, can be used as quick edit tool.
 */
export const QuickEditButton = forwardRef(
  (
    {
      children,
      title,
      open,
      onClick,
      onClose,
      fullWidth = true,
      boxProps,
      variant = "popover",
      error,
      unmountOnClose,
      paperProps,
    }: HtmlTooltipProps,
    ref: ForwardedRef<QuickEditButtonActions | undefined>
  ): ReactElement => {
    const anchorEl = useRef<HTMLDivElement>(null);
    const popoverRef = React.useRef<PopoverActions | null>(null);
    const [isOverflow, setIsOverflow] = React.useState(false);

    const overflowCallback = useCallback((overflow: boolean) => {
      if (overflow && popoverRef.current) {
        setIsOverflow(true);
      }
    }, []);

    const handleKeyDown = useCallback<KeyboardEventHandler>(
      (event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick?.();
        }
      },
      [onClick]
    );

    const onResize = useCallback((iOEntry: ResizeObserverEntry) => {
      if (
        anchorEl.current &&
        popoverRef.current &&
        iOEntry.target.getBoundingClientRect().top < anchorEl.current?.getBoundingClientRect().bottom
      ) {
        setIsOverflow(true);
      }
      if (popoverRef.current) {
        popoverRef.current.updatePosition();
      }
    }, []);

    const [paperRef, setPaperRef] = useElementResizeAndViewportOverflow<HTMLDivElement>({
      callback: overflowCallback,
      onResize: onResize,
    });

    useEffect(() => {
      if (popoverRef.current) {
        popoverRef.current.updatePosition();
      }
    }, [isOverflow]);

    useImperativeHandle(ref, () => ({
      shake() {
        if (paperRef.current) {
          paperRef.current.animate(
            [
              { transform: "translateX(0)" },
              { transform: "translateX(-8px)" },
              { transform: "translateX(8px)" },
              { transform: "translateX(-6px)" },
              { transform: "translateX(6px)" },
              { transform: "translateX(-4px)" },
              { transform: "translateX(4px)" },
              { transform: "translateX(0)" },
            ],
            { duration: 400, easing: "ease-in-out" }
          );
        }
      },
    }));

    return (
      <div>
        <EditableBox
          ref={anchorEl}
          error={error}
          focused={open}
          onClick={onClick}
          onKeyDown={handleKeyDown}
          role="button"
          sx={fullWidth ? {} : { width: "fit-content", display: "flex" }}
          tabIndex={0}
          {...boxProps}
        >
          {children}
        </EditableBox>
        {(open || !unmountOnClose) && variant === "menu" && (
          <Menu
            action={popoverRef}
            anchorEl={anchorEl.current}
            anchorOrigin={{ vertical: isOverflow ? "top" : "bottom", horizontal: "left" }}
            onClose={onClose}
            open={!!open}
            slotProps={{
              paper: {
                ref: setPaperRef,
                ...paperProps,
              },
            }}
            slots={{ transition: Fade }}
          >
            {title}
          </Menu>
        )}
        {(open || !unmountOnClose) && variant === "popover" && (
          <Popover
            action={popoverRef}
            anchorEl={anchorEl.current}
            anchorOrigin={{ vertical: isOverflow ? "top" : "bottom", horizontal: "left" }}
            onClose={onClose}
            open={!!open}
            slotProps={{
              paper: {
                ref: setPaperRef,
                elevation: 4,
                ...paperProps,
                sx: { p: 2, ...paperProps?.sx, borderRadius: "12px !important", overflow: "visible" },
              },
            }}
            slots={{ transition: Fade }}
            transformOrigin={{ vertical: isOverflow ? "bottom" : "top", horizontal: "left" }}
          >
            {title}
          </Popover>
        )}
      </div>
    );
  }
);
QuickEditButton.displayName = "QuickEditButton";
